/**
 * Dividend income — pure calculation logic.
 *
 * Gross (default) result:
 *   annualGrossDividend = invested × yield
 *   perPayment          = annualGrossDividend / paymentsPerYear
 *   totalGross          = annualGrossDividend × years
 *
 * Net result is OPTIONAL and country dependent. For a Bulgarian tax resident:
 *  - Bulgarian-source dividends: 5% final tax withheld (чл. 38, ал. 1 ЗДДФЛ).
 *  - Foreign dividends: the source country may withhold tax at its own rate,
 *    capped by the applicable double taxation treaty (СИДДО). Bulgaria taxes
 *    the gross dividend at 5% and, where the treaty provides for it, allows a
 *    tax credit for the foreign withholding, limited to the Bulgarian tax due.
 *  Treaty relief usually requires paperwork (e.g. W-8BEN for US stocks).
 */

export const DIVIDENDS_VERIFIED_AT = "2026-08-15";

export const BG_DIVIDEND_TAX_RATE = 0.05;

export type DividendFrequency = "yearly" | "semi-annually" | "quarterly" | "monthly";

export const PAYMENTS_PER_YEAR: Record<DividendFrequency, number> = {
  yearly: 1,
  "semi-annually": 2,
  quarterly: 4,
  monthly: 12,
};

export interface DividendInput {
  investedAmount: number;
  dividendYieldPct: number;
  years: number;
  frequency: DividendFrequency;
}

export interface DividendResult {
  annualGross: number;
  grossPerPayment: number;
  totalGross: number;
  paymentsPerYear: number;
}

export function calculateDividends({
  investedAmount,
  dividendYieldPct,
  years,
  frequency,
}: DividendInput): DividendResult {
  const annualGross = investedAmount * (dividendYieldPct / 100);
  const paymentsPerYear = PAYMENTS_PER_YEAR[frequency] ?? 1;
  return {
    annualGross,
    grossPerPayment: annualGross / paymentsPerYear,
    totalGross: annualGross * years,
    paymentsPerYear,
  };
}

export interface DividendTaxInput {
  annualGross: number;
  /** Withholding rate applied in the source country, in percent (treaty rate). */
  foreignWithholdingPct: number;
  /** Bulgarian tax rate on the gross dividend, in percent. */
  bgTaxPct?: number;
  /** Whether the treaty allows crediting the foreign withholding in Bulgaria. */
  treatyCreditAllowed?: boolean;
}

export interface DividendTaxResult {
  foreignWithholding: number;
  bgTaxBeforeCredit: number;
  taxCredit: number;
  bgTaxDue: number;
  totalTax: number;
  net: number;
  effectiveRatePct: number;
}

/**
 * Illustrative net-of-tax figure. The real outcome depends on the country of
 * the payer, the applicable СИДДО and whether a tax credit is granted.
 */
export function applyDividendTax({
  annualGross,
  foreignWithholdingPct,
  bgTaxPct = BG_DIVIDEND_TAX_RATE * 100,
  treatyCreditAllowed = true,
}: DividendTaxInput): DividendTaxResult {
  const foreignWithholding = annualGross * (foreignWithholdingPct / 100);
  const bgTaxBeforeCredit = annualGross * (bgTaxPct / 100);
  const taxCredit = treatyCreditAllowed
    ? Math.min(foreignWithholding, bgTaxBeforeCredit)
    : 0;
  const bgTaxDue = Math.max(0, bgTaxBeforeCredit - taxCredit);
  const totalTax = foreignWithholding + bgTaxDue;

  return {
    foreignWithholding,
    bgTaxBeforeCredit,
    taxCredit,
    bgTaxDue,
    totalTax,
    net: annualGross - totalTax,
    effectiveRatePct: annualGross > 0 ? (totalTax / annualGross) * 100 : 0,
  };
}
